/**
 * The API client. Every request goes to /api/v1 on this console's own origin (Vite in
 * development and nginx in Docker forward it to the backend), so no CORS is involved.
 *
 * Sessions: the short-lived access token lives only in memory; the refresh token is kept
 * in localStorage so a reload keeps you signed in. On a 401 the client refreshes once
 * (one refresh in flight, however many requests are waiting) and retries; if the refresh
 * fails the session ends and the app shows the login page.
 */

const BASE = "/api/v1";
const REFRESH_KEY = "newsroom.refresh";

let accessToken: string | null = null;
let refreshing: Promise<boolean> | null = null;
const listeners = new Set<() => void>();

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly body: unknown,
  ) {
    super(describe(body) || `Request failed (${status})`);
  }

  /** Field errors from a DRF 400, flattened to { field: "message" }. */
  get fields(): Record<string, string> {
    const out: Record<string, string> = {};
    if (this.body && typeof this.body === "object" && !Array.isArray(this.body)) {
      for (const [key, value] of Object.entries(this.body as Record<string, unknown>)) {
        if (key !== "detail") out[key] = describe(value);
      }
    }
    return out;
  }
}

function describe(body: unknown): string {
  if (!body) return "";
  if (typeof body === "string") return body;
  if (Array.isArray(body)) return body.map(describe).filter(Boolean).join(" ");
  if (typeof body === "object") {
    const record = body as Record<string, unknown>;
    if (record.detail) return describe(record.detail);
    return Object.entries(record)
      .map(([key, value]) => (key === "non_field_errors" ? describe(value) : `${key}: ${describe(value)}`))
      .join(" · ");
  }
  return String(body);
}

export const session = {
  hasRefresh: () => !!safeGet(REFRESH_KEY),
  set(access: string, refresh?: string) {
    accessToken = access;
    if (refresh) safeSet(REFRESH_KEY, refresh);
  },
  clear() {
    accessToken = null;
    safeRemove(REFRESH_KEY);
    listeners.forEach((listener) => listener());
  },
  refreshToken: () => safeGet(REFRESH_KEY),
  /** Called when the session ends unexpectedly (refresh rejected). */
  onExpired(listener: () => void) {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
};

function safeGet(key: string) {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}
function safeSet(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* private mode: the session simply won't survive a reload */
  }
}
function safeRemove(key: string) {
  try {
    localStorage.removeItem(key);
  } catch {
    /* nothing stored */
  }
}

/** Get a new access token with the stored refresh token. Shared by concurrent callers. */
export function refreshAccess(): Promise<boolean> {
  refreshing ??= (async () => {
    const refresh = safeGet(REFRESH_KEY);
    if (!refresh) return false;
    try {
      const response = await fetch(`${BASE}/auth/refresh/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh }),
      });
      if (!response.ok) return false;
      const data = (await response.json()) as { access: string; refresh?: string };
      session.set(data.access, data.refresh);
      return true;
    } catch {
      return false;
    }
  })().finally(() => {
    refreshing = null;
  });
  return refreshing;
}

type Query = Record<string, string | number | boolean | undefined | null | (string | number)[]>;

export interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  body?: unknown;
  query?: Query;
  signal?: AbortSignal;
}

export function buildUrl(path: string, query?: Query) {
  const url = new URL(`${BASE}${path}`, window.location.origin);
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value === undefined || value === null || value === "") continue;
    if (Array.isArray(value)) value.forEach((v) => url.searchParams.append(key, String(v)));
    else url.searchParams.set(key, String(value));
  }
  return url.pathname + url.search;
}

async function send(path: string, options: RequestOptions, retried = false): Promise<Response> {
  const isForm = typeof FormData !== "undefined" && options.body instanceof FormData;
  const headers: Record<string, string> = { Accept: "application/json" };
  if (options.body !== undefined && !isForm) headers["Content-Type"] = "application/json";
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;

  const response = await fetch(buildUrl(path, options.query), {
    method: options.method ?? "GET",
    headers,
    body: options.body === undefined ? undefined : isForm ? (options.body as FormData) : JSON.stringify(options.body),
    signal: options.signal,
  });

  if (response.status === 401 && !retried && path !== "/auth/login/") {
    if (await refreshAccess()) return send(path, options, true);
    session.clear();
  }
  return response;
}

export async function api<T = unknown>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await send(path, options);
  if (response.status === 204) return undefined as T;
  const text = await response.text();
  const body = text ? safeJson(text) : null;
  if (!response.ok) throw new ApiError(response.status, body);
  return body as T;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

/** Download a file from the API (CSV exports) with the reader's session. */
export async function download(path: string, fallbackName: string, query?: Query) {
  const response = await send(path, { query });
  if (!response.ok) throw new ApiError(response.status, safeJson(await response.text()));
  const blob = await response.blob();
  const disposition = response.headers.get("Content-Disposition") ?? "";
  const name = /filename="?([^";]+)"?/.exec(disposition)?.[1] ?? fallbackName;
  const url = URL.createObjectURL(blob);
  const link = Object.assign(document.createElement("a"), { href: url, download: name });
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
